import { usePatchAnalysisReviewUpdateStatus } from '@api/endpoints/analysis-reviews/patch-analysis-review-update-status'
import type { GroupVersionHistoryEntryPublic } from '@api/endpoints/analysis-reviews/post-analysis-review-group-version-history-schema'
import type { DocGroupPublic } from '@api/endpoints/workspaces/document-groups/get-workspace-document-group-schema'
import { getMinimapGroups } from '@api/review/getMinimapGroups'
import { parseReviewComponents } from '@api/review/parseReviewComponents'
import { type ReviewPublicFull, ReviewStatuses } from '@api/review/schema'
import { useDocGroupState } from '@core/hooks/useDocGroupState'
import { ReviewHistorySelect } from '@design/components/form/selects/review-history-select/ReviewHistorySelect'
import { Content } from '@design/components/layouts/content/Content'
import { ContentWithAppHeader } from '@design/components/layouts/content-with-app-header/ContentWithAppHeader'
import { ContentWithPdf } from '@design/components/layouts/content-with-pdf/ContentWithPdf'
import { Skeleton } from '@design/components/loaders/skeleton/Skeleton'
import { ErrorScreen } from '@design/components/screens/error-screen/ErrorScreen'
import { TableSkeleton } from '@design/components/tables/table-skeletons/table-skeleton/TableSkeleton'
import { ValidateReviewButton } from '@design/components/validate-review-button/ValidateReviewButton'
import { useAttributesStore } from '@design/stores/attributes-store'
import { useBreadcrumbStore } from '@design/stores/breadcrumb-store'
import { ReviewContext } from '@platform/contexts/review-context'
import { EmptyReviewFolderScreen } from '@platform/features/folders/EmptyReviewFolderScreen'
import { PendingReviewFolderScreen } from '@platform/features/folders/PendingReviewFolderScreen'
import { mapTodos } from '@platform/features/folders/utils/mapTodos'
import { reviewBreadcrumbs } from '@platform/features/folders/utils/reviewBreadcrumbs'
import {
  createFileRoute,
  Outlet,
  retainSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { type PropsWithChildren, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const folderSearchSchema = z.object({
  reviewId: z.string().optional().catch(undefined),
})

export const Route = createFileRoute(
  '/_authenticated/$workspaceId/reviews/$folderId'
)({
  component: RouteComponent,
  validateSearch: (search) => folderSearchSchema.parse(search),
  search: { middlewares: [retainSearchParams(['reviewId'])] },
})

function RouteComponent() {
  const { t } = useTranslation()
  const { workspaceId, folderId } = Route.useParams()
  const { reviewId } = Route.useSearch()

  const state = useDocGroupState(workspaceId, folderId, reviewId)

  if (state.kind === 'loading') {
    return (
      <FolderScreen>
        <Content>
          <Skeleton className='mb-4 h-8 w-96' />
          <TableSkeleton />
        </Content>
      </FolderScreen>
    )
  }

  if (state.kind === 'error') {
    return (
      <FolderScreen>
        <ErrorScreen message={t('folders.failedToLoadTheReview')} />
      </FolderScreen>
    )
  }

  if (state.kind === 'ready') {
    return (
      <ReviewLoaded
        docGroup={state.docGroup}
        review={state.review}
        versions={state.versions}
      />
    )
  }

  if (state.kind === 'pending') {
    return (
      <FolderScreen folderName={state.docGroup.name}>
        <PendingReviewFolderScreen
          workspaceId={workspaceId}
          docGroup={state.docGroup}
          version={state.version}
        />
      </FolderScreen>
    )
  }

  return (
    <FolderScreen folderName={state.docGroup.name}>
      <EmptyReviewFolderScreen
        workspaceId={workspaceId}
        docGroup={state.docGroup}
      />
    </FolderScreen>
  )
}

function FolderScreen({
  folderName,
  children,
}: PropsWithChildren<{ folderName?: string }>) {
  const { t } = useTranslation()

  useEffect(() => {
    const crumbs = reviewBreadcrumbs(t)

    useBreadcrumbStore
      .getState()
      .setBreadcrumbs(folderName ? crumbs.folder(folderName) : crumbs.root())
  }, [t, folderName])

  return <ContentWithAppHeader>{children}</ContentWithAppHeader>
}

type ReviewLoadedProps = {
  docGroup: DocGroupPublic
  review: ReviewPublicFull
  versions: GroupVersionHistoryEntryPublic[]
}

function ReviewLoaded({ docGroup, review, versions }: ReviewLoadedProps) {
  const { workspaceId } = Route.useParams()
  const navigate = useNavigate()

  const reviewId = review.id

  const minimapGroups = useMemo(
    () => getMinimapGroups(review.components),
    [review.components]
  )

  /**
   * So this useMemo is actually just because I needed to update the stores in a timely fashion
   * useEffect will only trigger after the component renders which is too late. I need it immediately.
   * And neither ReactRouter nor ReactQuery provides efficient ways of reacting to the data change.
   */
  const todos = useMemo(() => {
    // Initialization of stores when we have a review
    const { components, controls, id } = review
    const { componentMap, componentByComponentResult } =
      parseReviewComponents(components)

    useAttributesStore
      .getState()
      .init({ componentMap, componentByComponentResult, controls, id })

    const { controlsByComponent, controlMap, getComponents } =
      useAttributesStore.getState()

    return mapTodos({
      controlsByComponent,
      controlMap,
      componentExtracts: getComponents(),
    })
  }, [review])

  const {
    mutateAsync: updateReviewStatus,
    isPending: isValidateReviewPending,
    error: updateReviewStatusError,
  } = usePatchAnalysisReviewUpdateStatus()

  function validateReview() {
    return updateReviewStatus({
      params: { workspace_id: workspaceId, review_id: reviewId },
      payload: { status: ReviewStatuses.VALID },
    })
  }

  const isReviewReadOnly = review.status !== ReviewStatuses.IN_PROGRESS

  return (
    <ReviewContext.Provider
      value={{ docGroup, review, isReviewReadOnly, todos }}
    >
      <ContentWithPdf
        documents={review.documents}
        textExtractions={review.textExtractions}
        isReadOnly={isReviewReadOnly}
        minimapGroups={minimapGroups}
      >
        <ContentWithAppHeader
          headerAfter={
            <>
              <ReviewHistorySelect
                versions={versions}
                reviewId={reviewId}
                onHistoryChange={(id) =>
                  navigate({ to: '.', search: { reviewId: id } })
                }
                className='ml-auto w-min'
              />

              {!isReviewReadOnly && (
                <ValidateReviewButton
                  isLoading={isValidateReviewPending}
                  hasError={!!updateReviewStatusError}
                  onValidate={validateReview}
                />
              )}
            </>
          }
        >
          <Outlet />
        </ContentWithAppHeader>
      </ContentWithPdf>
    </ReviewContext.Provider>
  )
}
